import React from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Cpu,
  Wifi,
  ShieldCheck,
  Sparkles,
  Battery,
  Printer,
  Zap,
  Smartphone,
  Layout,
  Server,
  ChevronsUp,
  BookOpen,
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ProductPageLayout from '../components/ProductPageLayout';
import PaymentSchemes from '../components/PaymentSchemes';

const N96_PICTURE = '/products/n96.webp';
const N86_IMG = '/products/n86.webp';

// Reusable line-bordered feature box (replaces bullets — Teya-style)
const FeatureBox = ({ icon: Icon, title, description, highlight = false, testId }) => (
  <div
    className={`relative rounded-xl p-5 transition-all duration-200 ${
      highlight
        ? 'bg-white border-2 border-[#1F4E79]/35 shadow-md ring-1 ring-[#1F4E79]/10'
        : 'bg-white border border-gray-200 hover:border-[#1F4E79]/40 hover:shadow-sm'
    }`}
    data-testid={testId}
  >
    <div className="flex items-start gap-3.5">
      {Icon && (
        <div className={`flex-shrink-0 mt-0.5 ${highlight ? 'text-[#1F4E79]' : 'text-gray-500'}`}>
          <Icon size={22} strokeWidth={1.75} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h4 className="text-[15px] font-bold text-gray-900 leading-snug">{title}</h4>
        {description && (
          <p className="text-[13px] text-gray-600 leading-relaxed mt-1">{description}</p>
        )}
      </div>
    </div>
  </div>
);

const Section = ({ title, children, testId }) => (
  <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 mb-8" data-testid={testId}>
    {title && <h2 className="text-2xl font-bold text-gray-900 mb-4">{title}</h2>}
    {children}
  </section>
);

const SectionHeading = ({ children, testId }) => (
  <div className="mb-6" data-testid={testId}>
    <h2 className="text-3xl font-bold text-gray-900 mb-2">{children}</h2>
    <div className="h-1 w-20 bg-[#1F4E79] rounded-lg" />
  </div>
);

const CTAButton = ({ testId = 'pos-cta-button', children = 'Get Started Here' }) => (
  <Link
    to="/get-started"
    onClick={() => window.__resetSourceScroll?.()}
    className="group inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg"
    data-testid={testId}
  >
    {children}</Link>
);

// Internal manual link
const ManualLink = ({ to, label, backLabel, testId }) => (
  <Link
    to={to}
    state={{ backLabel, backPath: '/products/pos-card-machines' }}
    className="group inline-flex items-center gap-2 mt-5 text-[#1F4E79] hover:text-[#163a5f] font-semibold text-sm border-b border-[#1F4E79]/30 hover:border-[#1F4E79] pb-0.5 transition-colors duration-200"
    data-testid={testId}
  >
    <BookOpen size={16} />
    {label}</Link>
);

// Hero — clean, light, with the new N96 PICTURE (dual-angle on black bg)
const N96Hero = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromHome = ['/', 'footer'].includes(location.state?.from);
  const fromProductNav = location.state?.from === 'product-pagination';
  const productNavReturnTo = location.state?.returnTo;
  const fromTopNav = location.state?.from === 'top-nav';
  return (
    <section className="relative bg-[#f7f6f3]" data-testid="pos-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-10">
        <button
          onClick={() => {
            if (fromTopNav) navigate('/');
            else if (fromProductNav && productNavReturnTo) navigate(productNavReturnTo);
            else if (fromHome) navigate(-1);
            else navigate('/products');
          }}
          className="flex items-center gap-2 text-gray-600 hover:text-[#1F4E79] font-medium mb-8 transition-colors duration-200"
          data-testid="product-back-button"
        >
          <ArrowLeft size={18} />
          {fromTopNav ? 'Back to Home' : (fromHome || fromProductNav) ? 'Back' : 'Back to Products'}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left — title + subtitle + CTA (smaller column to give the N96 dominance) */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25]/10 border border-[#ef5b25]/30 text-[#c44516] text-[11px] font-bold uppercase tracking-[0.15em] mb-5">
              In-Store Payments
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-[88px] font-bold text-gray-900 leading-[1.05] tracking-tight mb-6" data-testid="product-title">
              POS Card<br />Machines
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-xl mb-9">
              Card machines built for today's customers — secure, reliable and future-ready payment terminals from our NEXGO range.
            </p>
            <CTAButton testId="hero-cta" />
          </div>

          {/* Right — N96 PICTURE, DOMINANT */}
          <div className="lg:col-span-7 order-1 lg:order-2 flex items-center justify-center">
            <img
              src={N96_PICTURE}
              alt="eCOMM N96 Android terminal — front and angled views"
              className="object-contain"
              style={{ maxWidth: '1100px', width: '100%', height: 'auto' }}
              loading="eager"
              fetchpriority="high"
              decoding="async"
              data-testid="hero-n96-image"
            />
          </div>
        </div>

        {/* Payment schemes glass-blue card — sits under the hero, above 'Our Terminal Range' */}
        <div className="mt-12">
          <PaymentSchemes testId="pos-payment-schemes" />
        </div>
      </div>
    </section>
  );
};

const ProductsPosCardMachines = () => (
  <ProductPageLayout
    currentId="pos-card-machines"
    customHero={<N96Hero />}
    bodyBgClassName="bg-[#f7f6f3]"
    mainTestId="products-pos-page"
  >
    {/* 1. Our Terminal Range */}
    <SectionHeading testId="pos-range-heading">Our Terminal Range</SectionHeading>

    {/* 2. N96 — Next Generation Android Terminal */}
    <Section testId="pos-n96">
      <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.15em] mb-2">Next Generation Android</p>
      <h3 className="text-2xl font-bold text-gray-900 mb-5">N96 — Next Generation Android Terminal</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FeatureBox icon={Layout}      title="Large colour touchscreen for intuitive operation" testId="n96-f1" />
        <FeatureBox icon={Cpu}         title="Advanced processing power for fast, reliable performance" testId="n96-f2" />
        <FeatureBox icon={Sparkles}    title="Modern design with a streamlined user interface" testId="n96-f3" />
        <FeatureBox icon={Wifi}        title="Enhanced connectivity with optimised battery performance" testId="n96-f4" />
        <FeatureBox icon={Server}      title="Built on proven NEXGO platform reliability" testId="n96-f5" />
        <FeatureBox icon={ShieldCheck} title="Enterprise-grade security for every transaction" testId="n96-f6" />
      </div>
      <ManualLink to="/n96-terminal-guide" label="View N96 User Guide" backLabel="Back to POS Card Machines" testId="n96-manual-link" />
    </Section>

    {/* 3. N86 — Flagship Android Terminal — BIG, no wrapper, just the image */}
    <div className="mb-8" data-testid="pos-n86">
      <div className="flex items-center justify-center mb-4" data-testid="n86-showcase">
        <img
          src={N86_IMG}
          alt="eCOMM N86 flagship Android terminal"
          className="object-contain"
          style={{ maxWidth: '480px', width: '100%', height: 'auto' }}
        />
      </div>

      <Section testId="pos-n86-features">
        <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.15em] mb-2">Flagship Android</p>
        <h3 className="text-2xl font-bold text-gray-900 mb-5">N86 — Flagship Android Terminal</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FeatureBox icon={Zap}     title="High-speed performance powered by quad-core processing" testId="n86-f1" />
          <FeatureBox icon={Wifi}    title="Dual connectivity for fast, reliable transactions anywhere" testId="n86-f2" />
          <FeatureBox icon={Printer} title="Built-in thermal printer for quick, seamless receipts" testId="n86-f3" />
          <FeatureBox icon={Battery} title="Long-lasting battery life to keep your business running" testId="n86-f4" />
        </div>
        <ManualLink to="/n86-terminal-guide" label="View N86 User Guide" backLabel="Back to POS Card Machines" testId="n86-manual-link" />
      </Section>
    </div>

    {/* 4. Key Features Across All Terminals */}
    <Section title="Key Features Across All Terminals" testId="pos-key-features">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FeatureBox icon={Server}      title="Android OS for flexibility and scalability" testId="kf-1" />
        <FeatureBox icon={ShieldCheck} title="PCI PTS 6.x certified for the highest level of payment security" testId="kf-2" />
        <FeatureBox icon={Wifi}        title="Optional charging base with integrated Ethernet for direct broadband connectivity" testId="kf-3" />
        <FeatureBox icon={ChevronsUp}  title="Cloud-based NEXGO Terminal Management System with App Marketplace" testId="kf-4" />
        <FeatureBox icon={Sparkles}    title="eCOMM-developed terminal software enabling frequent updates and custom features" testId="kf-5" />
        <FeatureBox icon={Cpu}         title="Advanced technology delivering exceptional performance" testId="kf-6" />
      </div>
    </Section>

    {/* 5. Why Choose eCOMM POS Terminals */}
    <Section title="Why Choose Our POS Terminals" testId="pos-why">
      <p className="text-gray-700 leading-relaxed">
        Built on Android OS for maximum flexibility, our terminals offer the perfect balance of performance, security and scalability. From countertop installations to mobile operations, we have the right solution for your business needs.
      </p>
    </Section>

    {/* 6. What Makes Us Different */}
    <Section title="What Makes Us Different" testId="pos-different">
      <p className="text-gray-700 leading-relaxed">
        Unlike standard terminal providers, eCOMM has developed and certified our own terminal software. This means faster updates; custom features tailored to your needs and MPOS integrations designed specifically for your operational requirements.
      </p>
    </Section>

    {/* Bottom CTA banner */}
    <section className="bg-gradient-to-br from-[#0a1929] to-[#163a5f] rounded-xl shadow-xl p-8 sm:p-12 mb-4 text-center" data-testid="pos-cta-banner">
      <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Ready To Upgrade Your In-Store Payments?</h2>
      <p className="text-white/85 leading-relaxed max-w-2xl mx-auto mb-6">
        Get a NEXGO terminal that fits your business — speak to our team and we'll match you with the right device and integration.
      </p>
      <Link
        to="/get-started"
    onClick={() => window.__resetSourceScroll?.()}
        className="group inline-flex items-center gap-2.5 px-7 py-3.5 bg-white text-[#1F4E79] font-bold rounded-lg hover:bg-gray-100 hover:gap-3.5 transition-all duration-200 shadow-md"
        data-testid="pos-cta-bottom"
      >
        Get Started Here</Link>
    </section>
  </ProductPageLayout>
);

export default ProductsPosCardMachines;
